import {GLOBAL} from '../deprecated/global';
import {type WCFieldsData} from '../../../@type/woocommerce/field-data';
import {type WCFieldInfo} from '../../../@type/woocommerce/location-info';
import {PeachPayCustomer} from '../reducers/peachPayCustomerReducer';

/**
 * This is a helper function because it is a pain with typescript to
 * deal with the "FormDataEntryValue" type
 */
export function formEntry<T extends string>(formData: FormData, key: string): T | string {
	if (formData.get(key) === null) {
		return '';
	}

	return formData.get(key) as unknown as T ?? '';
}

export function cleanFormFields(formInfoRecord: Record<string, string>): void {
	// eslint-disable-next-line guard-for-in
	for (const field in formInfoRecord) {
		const additionalFields = Object.entries(GLOBAL.phpData?.additional_fields ?? {}).map(([_, value]) => value);
		if (additionalFields.find(f => f.field_name === field) && formInfoRecord[field]) {
			continue;
		}

		const billingFields = Object.entries(GLOBAL.phpData?.billing_fields ?? {}).map(([_, value]) => value);
		if (billingFields.find(f => f.field_name === field) && formInfoRecord[field]) {
			continue;
		}

		const shippingFields = Object.entries(GLOBAL.phpData?.shipping_fields ?? {}).map(([_, value]) => value);
		if (shippingFields.find(f => f.field_name === field) && formInfoRecord[field]) {
			continue;
		}

		if (isDefaultField(field, 'shipping') || isDefaultField(field, 'billing')) {
			formInfoRecord[field] = '';
			continue;
		}

		if (formInfoRecord[field]) {
			continue;
		} else {
			// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
			delete formInfoRecord[field];
		}
	}
}

export function isDefaultField(name: string, section: string): boolean {
	const defaultFieldNames: string[] = [
		section + '_email',
		section + '_phone',
		section + '_first_name',
		section + '_last_name',
		section + '_company',
		section + '_address_1',
		section + '_address_2',
		section + '_postcode',
		section + '_city',
		section + '_state',
		section + '_country',
	];
	return defaultFieldNames.includes(name);
}

/**
 * A helper method that returns the target field required requirements.
 * @param name field name target
 * @param fieldData The peachpay field data that contians all of the field data.
 * @param fieldInfo The field info rules that is from woocommerce.
 * @returns True if the target field is required, false otherwise.
 */
export function isRequiredField(name: string, fieldData: WCFieldsData[], fieldInfo?: WCFieldInfo): boolean {
	let fieldDataInfo = false;
	// eslint-disable-next-line @typescript-eslint/no-for-in-array
	for (const orderNumber in fieldData) {
		if (name === fieldData[orderNumber]?.field_name) {
			fieldDataInfo = Boolean(fieldData[orderNumber]?.field_required);
		}
	}

	let countryLocaleRule = true;
	if (fieldInfo) {
		if (fieldInfo.required === undefined) {
			countryLocaleRule = true;
		} else {
			countryLocaleRule = fieldInfo?.required;
		}
	}

	/**
	 * Field editor data || WC country locale rule.
	 * 		true			false			= false
	 * 		true 			true			= true
	 * 		false			true 			= false
	 * 		false			false			= false
	 */
	return fieldDataInfo && countryLocaleRule;
}

/**
 * A helper method that returns the target field required requirements.
 * @param name field name target
 * @param fieldData The peachpay field data that contians all of the field data.
 * @param fieldInfo The field info rules that is from woocommerce.
 * @returns True if the target field is enable, false otherwise.
 */
export function isEnabledField(name: string, fieldData: WCFieldsData[], fieldInfo?: WCFieldInfo): boolean {
	let fieldDataInfo = false;
	// eslint-disable-next-line @typescript-eslint/no-for-in-array
	for (const orderNumber in fieldData) {
		if (name === fieldData[orderNumber]?.field_name) {
			fieldDataInfo = Boolean(fieldData[orderNumber]?.field_enable);
		}
	}

	let countryLocaleRuleNotHidden = true;
	if (fieldInfo) {
		if (fieldInfo.hidden === undefined) {
			countryLocaleRuleNotHidden = true;
		} else {
			countryLocaleRuleNotHidden = !fieldInfo?.hidden;
		}
	}

	/**
	 * Field editor data && WC country locale hidden rule.
	 * 		true 			true			= false
	 * 		true			false			= true
	 * 		false			true 			= false
	 * 		false			false			= false
	 */
	return fieldDataInfo && countryLocaleRuleNotHidden;
}

// eslint-disable-next-line complexity
export function isShortAddressEmpty(section: string): boolean {
	const defaultShortAddress: string[] = [
		section + '_postcode',
		section + '_city',
		section + '_state',
		section + '_country',
	];

	const info = (section === 'billing') ? PeachPayCustomer.billing : PeachPayCustomer.shipping;
	const fields = (section === 'billing') ? GLOBAL.phpData?.billing_fields : GLOBAL.phpData?.shipping_fields;
	const fieldPostalCodeInfo = GLOBAL.phpData?.wc_location_info?.country_locale_data[info.country()]?.['postcode'];
	const fieldCityInfo = GLOBAL.phpData?.wc_location_info?.country_locale_data[info.country()]?.['city'];
	const fieldStateInfo = GLOBAL.phpData?.wc_location_info?.country_locale_data[info.country()]?.['state'];

	const shippingPostalCode = PeachPayCustomer.shipping.postal();
	const shippingCity = PeachPayCustomer.shipping.city();
	const shippingState = PeachPayCustomer.shipping.state();
	const shippingCountry = PeachPayCustomer.shipping.country();

	if (defaultShortAddress[0] && shippingPostalCode === '' && isEnabledField(defaultShortAddress[0], fields ?? [], fieldPostalCodeInfo) && isRequiredField(defaultShortAddress[0], fields ?? [], fieldPostalCodeInfo)) {
		return true;
	}

	if (defaultShortAddress[1] && shippingCity === '' && isEnabledField(defaultShortAddress[1], fields ?? [], fieldCityInfo) && isRequiredField(defaultShortAddress[1], fields ?? [], fieldCityInfo)) {
		return true;
	}

	if (defaultShortAddress[2] && shippingState === '' && isEnabledField(defaultShortAddress[2], fields ?? [], fieldStateInfo) && isRequiredField(defaultShortAddress[2], fields ?? [], fieldStateInfo)) {
		return true;
	}

	if (defaultShortAddress[3] && shippingCountry === '' && isEnabledField(defaultShortAddress[3], fields ?? []) && isRequiredField(defaultShortAddress[3], fields ?? [])) {
		return true;
	}

	return false;
}
